import { Transition, StateService, UrlService } from '@uirouter/angularjs';

import { userAdminCheck } from '@api/sdk.gen';

import { isAxiosError } from '@/react/portainer/services/axios/utils/isAxiosError';
import { captureReturnUrlFromQuery } from '@/react/portainer/helpers/returnUrl';

import { followReturnUrl } from '../helpers/url-utils';
import { AsyncService, IAuthenticationService } from '../services/types';

import { resolvePostLoginTarget } from './postLoginNavigation';

const NOT_FOUND_STATUS = 404;

interface LicenseService {
  resetState(): void;
  info(): Promise<{ valid: boolean }>;
}

/**
 * Guards the `portainer.auth` (login) state, sending the user away when the
 * login page shouldn't be shown: to the admin init flow when no admin exists
 * yet, or to their post-login target when they are already authenticated.
 */
export function authPageRedirect(transition: Transition) {
  const injector = transition.injector();
  const $async: AsyncService = injector.get('$async');
  const $state: StateService = injector.get('$state');
  const $urlService: UrlService = injector.get('$urlService');
  const authService: IAuthenticationService = injector.get('Authentication');
  const licenseService: LicenseService = injector.get('LicenseService');

  return $async(async () => {
    const adminExists = await checkAdminExists();
    if (!adminExists) {
      return $state.target('portainer.init.admin', undefined, { reload: true });
    }

    // Capture ?returnUrl= before checking auth: an already-authenticated
    // visitor never mounts LoginView, so its own capture effect never runs.
    captureReturnUrlFromQuery();

    const isLoggedIn = await authService.init();
    if (!isLoggedIn) {
      return undefined;
    }

    const target = await resolvePostLoginTarget({
      Authentication: authService,
      LicenseService: licenseService,
    });
    if ('url' in target) {
      if (followReturnUrl($urlService, $state, window, target.url)) {
        return undefined;
      }
      return $state.target('portainer.home', undefined, { reload: true });
    }

    return $state.target(target.state, undefined, { reload: true });
  });
}

async function checkAdminExists() {
  // The endpoint returns 204 when an admin exists and 404 when none does; any
  // other failure is a real error and should propagate.
  try {
    await userAdminCheck();
    return true;
  } catch (err) {
    if (isAxiosError(err) && err.response?.status === NOT_FOUND_STATUS) {
      return false;
    }

    throw err;
  }
}
